import { useMemo, useState } from 'react';
import { useController } from 'react-hook-form';
import type { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { HOUSEHOLD_TAXABLE_INCOME_CAP, INCOME_OVER_CAP_ERROR } from 'src/utils/constants';
import { Form, SelectField } from '@brighte/form-components';
import { buildDeclareTaxableIncomeFormSchema, DeclareTaxableIncomeFormType } from '@brighte/validation';
import { PropertyTitleOwnerOutput } from '@graphql/index';
import { Accordion, AccordionItem } from '@spark-web/accordion';
import { Alert } from '@spark-web/alert';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { CurrencyInput } from '@spark-web/currency-input';
import { Field } from '@spark-web/field';
import { Heading } from '@spark-web/heading';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import EligibilityPageHeading from './EligibilityPageHeading';
import EligibilityStepHeader from './EligibilityStepHeader';
import { INCOME_TYPE_OPTIONS, INCOME_TYPES } from './incomeTypes';

type DeclareTaxableIncomeProps = {
  owners: PropertyTitleOwnerOutput[];
  productName: string;
  initialIncomes?: Record<string, string>;
  initialIncomeTypes?: Record<string, string>;
  onConfirm: (incomes: Record<string, string>, incomeTypes: Record<string, string>) => void;
  onBack: () => void;
};

function getInitials(fullName: string): string {
  return fullName
    .split(' ')
    .slice(0, 2)
    .map(word => word[0]?.toUpperCase() ?? '')
    .join('');
}

const AVATAR_BACKGROUNDS: Array<'primary' | 'secondary'> = ['primary', 'secondary'];

type DeclaredIncomeFieldName = `declaredIncomes.${string}`;

type OwnerIncomeAmountFieldProps = {
  name: DeclaredIncomeFieldName;
  control: Control<DeclareTaxableIncomeFormType>;
  disabled: boolean;
  // Bumping this remounts *only* the inner currency input so its display re-seeds from the current
  // form value. spark's FloatInput snapshots its display on mount and ignores later external value
  // changes, so resetting the amount to $0 (on "No income") wouldn't otherwise show. useController
  // lives here and never remounts, so the field stays registered across the inner remount.
  resetKey: string;
};

// Thin local copy of form-components' CurrencyInputField that additionally accepts a resetKey to
// force the display to re-seed — the shared component can't reset an already-typed spark input.
function OwnerIncomeAmountField({ name, control, disabled, resetKey }: OwnerIncomeAmountFieldProps) {
  const {
    field: { value, onChange, ...field },
    fieldState: { error },
  } = useController<DeclareTaxableIncomeFormType, DeclaredIncomeFieldName>({ name, control, shouldUnregister: false });
  const [isPristine, setIsPristine] = useState(true);

  const handleChange = (nextValue: string | number) => {
    if (isPristine) {
      setIsPristine(false);
    }
    onChange(typeof nextValue === 'string' ? 0 : nextValue);
  };

  return (
    <Field
      label="Annual taxable income"
      disabled={disabled}
      tone={error ? 'critical' : undefined}
      message={error?.message}
    >
      {/* Show a blank placeholder for an untouched *editable* field, but never blank out a
          disabled field: "No income" forces the value to 0, and 0 must render as $0, not empty. */}
      <CurrencyInput
        key={resetKey}
        placeholder="0.00"
        {...field}
        value={isPristine && !value && !disabled ? '' : value}
        onChange={handleChange}
      />
    </Field>
  );
}

type OwnerIncomeRowProps = {
  owner: PropertyTitleOwnerOutput;
  index: number;
  control: Control<DeclareTaxableIncomeFormType>;
  watch: UseFormWatch<DeclareTaxableIncomeFormType>;
  setValue: UseFormSetValue<DeclareTaxableIncomeFormType>;
};

function OwnerIncomeRow({ owner, index, control, watch, setValue }: OwnerIncomeRowProps) {
  const isNoIncome = watch(`incomeTypes.${owner.id}`) === INCOME_TYPES.NO_INCOME;

  return (
    <Box borderRadius="medium" padding="large" borderWidth="standard" border="standard">
      <Stack gap="medium">
        <Box
          borderRadius="full"
          background={AVATAR_BACKGROUNDS[index] ?? 'secondary'}
          display="flex"
          alignItems="center"
          justifyContent="center"
          style={{ width: 40, height: 40 }}
        >
          <Text weight="semibold" size="small">
            {getInitials(owner.fullName)}
          </Text>
        </Box>

        {/* Extra separation between the name and the fields below: 8px on mobile (≤740px),
            16px on desktop. */}
        <Box marginBottom={{ mobile: 'small', tablet: 'large' }}>
          <Text weight="semibold" size="xlarge">
            {owner.fullName}
          </Text>
        </Box>

        <Box marginTop="small">
          <OwnerIncomeAmountField
            name={`declaredIncomes.${owner.id}`}
            control={control}
            disabled={isNoIncome}
            resetKey={isNoIncome ? 'no-income' : 'editable'}
          />
        </Box>

        <Box marginTop="small">
          <SelectField
            name={`incomeTypes.${owner.id}`}
            control={control}
            label="Income type"
            placeholder="Select"
            options={INCOME_TYPE_OPTIONS}
            onChange={event => {
              // "No income" is $0 by definition — reset the declared amount so no stale figure lingers.
              if (event.target.value === INCOME_TYPES.NO_INCOME) {
                setValue(`declaredIncomes.${owner.id}`, 0, { shouldValidate: true });
              }
            }}
          />
        </Box>
      </Stack>
    </Box>
  );
}

function DeclareTaxableIncome({
  owners,
  productName,
  initialIncomes,
  initialIncomeTypes,
  onConfirm,
  onBack,
}: DeclareTaxableIncomeProps) {
  const schema = useMemo(() => buildDeclareTaxableIncomeFormSchema(owners.map(owner => owner.id)), [owners]);

  const defaultValues = useMemo<DeclareTaxableIncomeFormType>(
    () => ({
      declaredIncomes: Object.fromEntries(
        owners.map(owner => {
          // "No income" owners are $0 by definition — never carry a stale declared amount.
          if (initialIncomeTypes?.[owner.id] === INCOME_TYPES.NO_INCOME) {
            return [owner.id, 0];
          }
          const initial = initialIncomes?.[owner.id];
          return [owner.id, initial != null && initial !== '' ? Number(initial) : (undefined as unknown as number)];
        }),
      ),
      incomeTypes: Object.fromEntries(owners.map(owner => [owner.id, initialIncomeTypes?.[owner.id] ?? ''])),
    }),
    [owners, initialIncomes, initialIncomeTypes],
  );

  const handleFormSubmit = (data: DeclareTaxableIncomeFormType) => {
    onConfirm(
      Object.fromEntries(owners.map(owner => [owner.id, String(data.declaredIncomes[owner.id] ?? 0)])),
      Object.fromEntries(owners.map(owner => [owner.id, data.incomeTypes[owner.id]])),
    );
  };

  return (
    <Form<DeclareTaxableIncomeFormType> schema={schema} defaultValues={defaultValues} onSubmit={handleFormSubmit}>
      {({ control, watch, setValue }) => {
        const values = watch();
        const totalDeclaredIncome = owners.reduce(
          (total, owner) => total + (Number(values.declaredIncomes?.[owner.id]) || 0),
          0,
        );
        const exceedsIncomeCap = totalDeclaredIncome > HOUSEHOLD_TAXABLE_INCOME_CAP;
        // A household where every owner declares "No income" isn't eligible for the program.
        const allOwnersNoIncome =
          owners.length > 0 && owners.every(owner => values.incomeTypes?.[owner.id] === INCOME_TYPES.NO_INCOME);
        const isIneligible = exceedsIncomeCap || allOwnersNoIncome;

        return (
          <Stack padding={{ tablet: 'xlarge' }} gap="xxlarge">
            <EligibilityStepHeader currentStep={4} totalSteps={5} />

            <EligibilityPageHeading productName={productName}>
              <Heading level="1">Declare taxable income</Heading>
            </EligibilityPageHeading>

            <Stack gap="large">
              {owners.map((owner, index) => (
                <OwnerIncomeRow
                  key={owner.id}
                  owner={owner}
                  index={index}
                  control={control}
                  watch={watch}
                  setValue={setValue}
                />
              ))}

              <Accordion type="multiple">
                <AccordionItem value="q1" label="Why do I have to declare my income?" level="4">
                  <Text>
                    The NSW Government caps household income at $210,000 after tax for this program. We need to confirm
                    the combined income of everyone on the property title to check your household qualifies. The figure
                    you enter here is self-declared and is only used for this eligibility check - it won&apos;t be used
                    to assess your loan application.
                  </Text>
                </AccordionItem>
                <AccordionItem value="q2" label="What can I do if my income is above $210,000?" level="4">
                  <Text>
                    If your income is above $210,000, unfortunately your household won&apos;t be eligible for this
                    program. Contact your vendor - they can help you explore other Brighte finance options for your home
                    upgrade.
                  </Text>
                </AccordionItem>
              </Accordion>

              {isIneligible && (
                <Alert tone="critical">
                  <Text size="small">{INCOME_OVER_CAP_ERROR}</Text>
                </Alert>
              )}

              <Stack gap="large">
                <Button type="submit" tone="primary" disabled={isIneligible}>
                  Confirm
                </Button>
                <Button type="button" prominence="none" tone="neutral" onClick={onBack}>
                  Back
                </Button>
              </Stack>
            </Stack>
          </Stack>
        );
      }}
    </Form>
  );
}

export default DeclareTaxableIncome;
